import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  if (isAuthenticated) {
    navigate(from, { replace: true });
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(formData.username, formData.password);

      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[rgb(0,84,128)] flex-col items-center justify-center relative">
        <div className="text-center">
          <img
            src="/images/logos/WRG_Logo.10cdae78427294f0d001.png"
            alt="World Racing Group"
            className="w-64 mx-auto mb-6"
          />
          <p className="text-white text-xl tracking-wide">Race Management System</p>
        </div>

        {/* Bottom logos */}
        <div className="absolute bottom-8 left-0 right-0 flex justify-center items-center gap-12 px-8">
          <img
            src="/images/logos/DIRTcar.2b3339a039bc332dd2af.png"
            alt="DIRTcar"
            className="h-8 opacity-90"
          />
          <img
            src="/images/logos/WorldOfOutlaws.1911254c36a55341c2ac.png"
            alt="World of Outlaws"
            className="h-10 opacity-90"
          />
          <img
            src="/images/logos/DIRTVision.ea7648751b1e4b46346c.png"
            alt="DIRTVision"
            className="h-8 opacity-90"
          />
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center bg-white px-8 py-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden mb-8 text-center">
            <img
              src="/images/logos/WRG_Logo.10cdae78427294f0d001.png"
              alt="World Racing Group"
              className="w-48 mx-auto mb-4"
            />
          </div>

          <h1 className="text-3xl font-semibold text-gray-900 mb-8 text-center">
            Welcome back
          </h1>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <input
                id="username"
                name="username"
                type="text"
                value={formData.username}
                onChange={handleChange}
                required
                autoComplete="username"
                autoFocus
                disabled={loading}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="Email"
              />
            </div>

            <div>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
                disabled={loading}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="Password"
              />
            </div>

            <div className="text-left">
              <a href="#" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                Forgot password?
              </a>
            </div>

            <button
              type="submit"
              disabled={loading || !formData.username || !formData.password}
              className="w-full bg-gray-900 hover:bg-gray-800 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Continue'}
            </button>
          </form>

          <div className="mt-8 text-center">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">or</span>
              </div>
            </div>

            <button
              type="button"
              className="mt-6 w-full border border-gray-300 text-gray-700 font-medium py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              onClick={() => {/* TODO: Activate account flow */}}
            >
              Activate account
            </button>
          </div>

          {/* Warning text */}
          <p className="mt-12 text-xs text-red-600 text-center">
            * Any unauthorized access to this proprietary software suite is subject to fines and imprisonment.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
